import React, {useCallback, useMemo, useRef} from 'react';
import useShow from '../../../hooks/useShow';
import setGlobalState from '../../../redux/setGlobalState';
import store from '../../../store';
import ClearingFeeMessagePop from '../../../layout/topMenu/clearingFeeMessagePop/ClearingFeeMessagePop';

/**
 * Gate create-event flow behind the same clearing-fee notice as the + create button.
 * Resolves true after confirm, false if the popup is dismissed.
 */
export function useAiAgentClearingFeeGate() {
    const feeShow = useShow();
    const resolveRef = useRef<((ok: boolean) => void) | null>(null);

    const settle = useCallback((ok: boolean) => {
        const resolve = resolveRef.current;
        resolveRef.current = null;
        if(ok) {
            setGlobalState('aiAgent.clearingFeeAcknowledged', true);
        }
        void feeShow.close();
        resolve?.(ok);
    }, [feeShow]);

    const feeShowWithDismiss = useMemo(() => ({
        ...feeShow,
        close: async (e?: Event | React.SyntheticEvent) => {
            await feeShow.close(e);
            if(resolveRef.current) {
                const resolve = resolveRef.current;
                resolveRef.current = null;
                resolve(false);
            }
        },
    }), [feeShow]);

    const ensureClearingFeeAcknowledged = useCallback((): Promise<boolean> => {
        if(store.getState().aiAgent?.clearingFeeAcknowledged) {
            return Promise.resolve(true);
        }
        return new Promise((resolve) => {
            resolveRef.current = resolve;
            feeShow.open();
        });
    }, [feeShow]);

    const resetClearingFeeAck = useCallback(() => {
        setGlobalState('aiAgent.clearingFeeAcknowledged', false);
        resolveRef.current = null;
    }, []);

    const clearingFeePop = feeShow.isOpen ? (
        <ClearingFeeMessagePop
            show={feeShowWithDismiss}
            onClick={() => settle(true)}
        />
    ) : null;

    return {
        ensureClearingFeeAcknowledged,
        resetClearingFeeAck,
        clearingFeePop,
    };
}
